// Code-defined router (vs file-based) for P1/P2 — fewer moving parts. Switch to
// file routes when the route count grows past ~15.

import { createRootRoute, createRoute, createRouter, Outlet } from "@tanstack/solid-router";

import { AccountDetailPage } from "./routes/AccountDetailPage";
import { AccountsPage } from "./routes/AccountsPage";
import { AuthCallbackPage } from "./routes/AuthCallbackPage";
import { ContactDetailPage } from "./routes/ContactDetailPage";
import { ContactsPage } from "./routes/ContactsPage";
import { CustomRecordDetailPage } from "./routes/CustomRecordDetailPage";
import { CustomRecordsPage } from "./routes/CustomRecordsPage";
import { DealDetailPage } from "./routes/DealDetailPage";
import { DealsPage } from "./routes/DealsPage";
import { HomeRedirect } from "./routes/HomeRedirect";
import { LoginPage } from "./routes/LoginPage";
import { PartnerAdminPage } from "./routes/PartnerAdminPage";
import { PipelineAdminPage } from "./routes/PipelineAdminPage";
import { ReportsPage } from "./routes/ReportsPage";
import { ProductsPage } from "./routes/ProductsPage";
import { SearchResultsPage } from "./routes/SearchResultsPage";
import { TasksPage } from "./routes/TasksPage";
import { WorkspaceHomePage } from "./routes/WorkspaceHomePage";
import { WorkspaceSettingsPage } from "./routes/WorkspaceSettingsPage";
import { WorkspacesPage } from "./routes/WorkspacesPage";

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: HomeRedirect,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
});

const authCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/callback",
  component: AuthCallbackPage,
});

const workspacesIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/workspaces",
  component: WorkspacesPage,
});

const workspaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/workspaces/$wsKey",
  component: () => <Outlet />,
});

const workspaceHomeRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/",
  component: WorkspaceHomePage,
});

const accountsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/accounts",
  component: AccountsPage,
});

const accountDetailRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/accounts/$accId",
  component: AccountDetailPage,
});

const contactsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/contacts",
  component: ContactsPage,
});

const contactDetailRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/contacts/$conId",
  component: ContactDetailPage,
});

const dealsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/deals",
  component: DealsPage,
});

const dealDetailRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/deals/$dealId",
  component: DealDetailPage,
});

const tasksRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/tasks",
  component: TasksPage,
});

const productsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/products",
  component: ProductsPage,
});

const customRecordsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/custom/$objectKey",
  component: CustomRecordsPage,
});

const customRecordDetailRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/custom/$objectKey/$recordId",
  component: CustomRecordDetailPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/settings",
  component: WorkspaceSettingsPage,
});

const pipelineAdminRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/settings/pipelines",
  component: PipelineAdminPage,
});

const searchResultsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/search",
  component: SearchResultsPage,
});

const reportsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/reports",
  component: ReportsPage,
});

const partnerAdminRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/partner-admin",
  component: PartnerAdminPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  authCallbackRoute,
  workspacesIndexRoute,
  workspaceRoute.addChildren([
    workspaceHomeRoute,
    accountsRoute,
    accountDetailRoute,
    contactsRoute,
    contactDetailRoute,
    dealsRoute,
    dealDetailRoute,
    productsRoute,
    customRecordsRoute,
    customRecordDetailRoute,
    tasksRoute,
    settingsRoute,
    pipelineAdminRoute,
    searchResultsRoute,
    reportsRoute,
    partnerAdminRoute,
  ]),
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/solid-router" {
  interface Register {
    router: typeof router;
  }
}
